import { useMemo } from 'react'
import styled from '@emotion/styled'
import DOMPurify from 'dompurify'

import { DefaultElementProps } from 'common/interfaces'

import { RichText as RichTextProps } from './interface'

const allowedRichTextRoles = ['none', 'presentation']
const legalTermsPattern = /Terms (?:of Service|& Conditions|and Conditions)/

function sanitizeRichTextHtml(html: string): string {
  const removeUnsupportedRoles = (node: Element): void => {
    const role = node.getAttribute('role')

    if (role !== null && !allowedRichTextRoles.includes(role)) {
      node.removeAttribute('role')
    }
  }

  DOMPurify.addHook('afterSanitizeAttributes', removeUnsupportedRoles)

  try {
    return DOMPurify.sanitize(html, {
      ADD_ATTR: ['role'],
      USE_PROFILES: { html: true },
    })
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes')
  }
}

function getNextMeaningfulSibling(node: ChildNode): ChildNode | null {
  let nextSibling = node.nextSibling

  while (nextSibling?.nodeType === Node.TEXT_NODE && nextSibling.textContent?.trim() === '') {
    nextSibling = nextSibling.nextSibling
  }

  return nextSibling
}

function mergeAdjacentLists(container: Element | DocumentFragment): void {
  Array.from(container.children).forEach((child) => mergeAdjacentLists(child))

  Array.from(container.children).forEach((child) => {
    if (!['OL', 'UL'].includes(child.tagName)) {
      return
    }

    let nextSibling = getNextMeaningfulSibling(child)

    while (nextSibling instanceof HTMLElement && nextSibling.tagName === child.tagName) {
      while (nextSibling.firstChild !== null) {
        child.appendChild(nextSibling.firstChild)
      }

      const siblingToRemove = nextSibling
      nextSibling = getNextMeaningfulSibling(siblingToRemove)
      siblingToRemove.remove()
    }
  })
}

function removeLegalCopyHeadingSemantics(container: Element | DocumentFragment): void {
  container.querySelectorAll('h6').forEach((heading) => {
    const text = heading.textContent ?? ''

    if (
      text.includes('I Agree and Continue') &&
      text.includes('Privacy Policy') &&
      legalTermsPattern.test(text)
    ) {
      heading.setAttribute('role', 'presentation')
    }
  })
}

function normalizeSemanticHtml(html: string): string {
  const sanitizedHtml = sanitizeRichTextHtml(html)

  if (typeof DOMParser === 'undefined') {
    return sanitizedHtml
  }

  const parsedDocument = new DOMParser().parseFromString(sanitizedHtml, 'text/html')

  mergeAdjacentLists(parsedDocument.body)
  removeLegalCopyHeadingSemantics(parsedDocument.body)

  return sanitizeRichTextHtml(parsedDocument.body.innerHTML)
}

export default function RichText({
  html,
  style,
}: RichTextProps & DefaultElementProps): JSX.Element {
  const StyledDiv = styled.div(style ?? '')
  const normalizedHtml = useMemo(() => normalizeSemanticHtml(html), [html])

  return <StyledDiv dangerouslySetInnerHTML={{ __html: normalizedHtml }} />
}
